import * as React from 'react';
import { MicIcon, MicOffIcon, Volume2Icon, VolumeXIcon, SquareIcon, MessageSquarePlusIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AgentVoice } from './voice/agent-voice';

type VoiceProps = Omit<React.ComponentProps<typeof AgentVoice>, 'beforeControls' | 'afterControls' | 'help'>;
export interface AgentFooterProps {
  files?: React.ReactNode;
  errors?: readonly (string | undefined)[];
  listening: boolean;
  readAloud: boolean;
  transcriptionSupported: boolean;
  synthesisSupported: boolean;
  dictationDisabled: boolean;
  newChatDisabled: boolean;
  onDictation: () => void;
  onReadAloud: () => void;
  onStopReadAloud: () => void;
  onNewChat: () => void;
  hidePlayback?: boolean;
  hintId: string;
  voice?: VoiceProps;
  availability?: string;
}
/** One footer ordering and accessibility contract for personal and record-shared hosts. */
export function AgentFooter(props: AgentFooterProps) {
  const dictation = <Button type="button" size="icon" className="size-8" variant={props.listening ? 'secondary' : 'outline'}
    disabled={props.dictationDisabled} onClick={props.onDictation} aria-label={props.listening ? 'Stop listening' : 'Speak to Agent'}
    title={props.listening ? 'Stop dictation' : 'Dictate into the message box'} aria-pressed={props.listening} aria-describedby={props.hintId}>
    {props.listening ? <MicOffIcon className="size-4" /> : <MicIcon className="size-4" />}</Button>;
  const after = !props.hidePlayback && <>
    <Button type="button" size="icon" className="size-8" variant={props.readAloud ? 'secondary' : 'outline'}
      role="switch" aria-checked={props.readAloud} data-state={props.readAloud ? 'checked' : 'unchecked'} aria-label="Read aloud Agent replies"
      title={props.readAloud ? 'Read aloud: on' : 'Read aloud: off'} disabled={!props.synthesisSupported} onClick={props.onReadAloud}>
      {props.readAloud ? <Volume2Icon className="size-4" /> : <VolumeXIcon className="size-4" />}</Button>
    <Button type="button" size="icon" className="size-8" variant="outline" aria-label="Stop read aloud" title="Stop read aloud" disabled={!props.synthesisSupported}
      onClick={props.onStopReadAloud}><SquareIcon className="size-4" /></Button>
    <Button type="button" size="icon" className="size-8" variant="outline" aria-label="Start new chat" title="Start new thread"
      disabled={props.newChatDisabled} onClick={props.onNewChat}><MessageSquarePlusIcon className="size-4" /></Button>
  </>;
  const help = <div id={props.hintId} className="sr-only" aria-live="polite">
    <p>Dictation fills the message box. Review it before sending.</p>
    {props.listening && <p>Listening… Select stop when you are finished.</p>}
    {!props.transcriptionSupported && <p>Voice input is not available in this browser. You can keep typing.</p>}
    {!props.hidePlayback && !props.synthesisSupported && <p>Read aloud is not available in this browser.</p>}
    {props.availability && <p>{props.availability}</p>}
  </div>;
  const errors = [...new Set(props.errors?.filter((message): message is string => Boolean(message)))];
  return <>
    {props.voice ? <AgentVoice {...props.voice} beforeControls={<>{props.files}{dictation}</>} afterControls={after} help={help} /> : <>
    <div role="group" aria-label="Agent voice controls" className="flex min-w-0 items-center gap-1 overflow-x-auto pb-1">{props.files}{dictation}{after}</div>
    {help}
  </>}
    {errors.map(message => <p key={message} role="alert" className="break-words text-xs text-destructive">{message}</p>)}
  </>;
}
